import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Zap, Check, Pencil, CalendarDays, ExternalLink, Users, Menu, LayoutDashboard, Clapperboard, Video, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { syncGoogleEvents } from "@/lib/googleWorkspace";
import { etapaInfo, fmtData, SAUDE, STATUS_CICLO, TIPOS_CICLO, STATUS_GRAVACAO, tipoEventoInfo, POST_STATUS } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { CicloDrawer } from "@/components/CicloDrawer";
import { ProducaoClientesTable } from "@/components/ProducaoClientesTable";
import { ProducaoResumo } from "@/components/ProducaoResumo";
import { ProducaoKanban } from "@/components/ProducaoKanban";
import { GravacoesCalendar } from "@/components/GravacoesCalendar";
import Hoje from "@/pages/Hoje";
import Clientes from "@/pages/Clientes";

const emptyCiclo = { titulo: "", cliente_id: "", competencia: "", tipo: "mensal", responsavel: "", entrega_prevista: "", template_id: "" };
const PRODUCTION_VIEWS = [
  { key: "hoje", label: "Hoje", description: "O que precisa de ação agora", icon: LayoutDashboard },
  { key: "pipeline", label: "Pipeline", description: "Todos os ciclos em andamento", icon: Clapperboard },
  { key: "clientes", label: "Clientes", description: "Carteira e operação por cliente", icon: Users },
  { key: "gravacoes", label: "Agenda da produção", description: "Rotina completa do time e dos clientes", icon: Video },
  { key: "edicao", label: "Edição", description: "Trabalhos em pós-produção", icon: Pencil },
  { key: "validacao", label: "Validação e entrega", description: "Cliente, aprovação e publicação", icon: CheckCircle2 },
  { key: "agenda", label: "Agenda", description: "Compromissos sincronizados", icon: CalendarDays },
];
const VIEWS = PRODUCTION_VIEWS.map((item) => item.key);
const LEGACY_VIEWS = { aprovacao: "validacao", publicacao: "validacao", aguardando: "validacao", capacidade: "hoje" };

export default function Producao() {
  const [ciclos, setCiclos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [postagens, setPostagens] = useState([]);
  const [cicloAberto, setCicloAberto] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyCiclo);
  const [salvando, setSalvando] = useState(false);
  const [gravacaoEdit, setGravacaoEdit] = useState(null);
  const [gravacaoForm, setGravacaoForm] = useState(null);
  const [google, setGoogle] = useState(null);
  const [sincronizandoGoogle, setSincronizandoGoogle] = useState(false);
  const [menuViewsOpen, setMenuViewsOpen] = useState(false);
  const [pipelineFormat, setPipelineFormat] = useState(() => {
    // "clientes" era a tabela detalhada; o resumo passa a ser o padrão.
    try { const saved = localStorage.getItem("netzer:producao:format:v2"); return ["resumo", "kanban", "tabela"].includes(saved) ? saved : "resumo"; }
    catch { return "resumo"; }
  });
  const [pipelineReady, setPipelineReady] = useState(false);
  const [pipelineError, setPipelineError] = useState("");

  const trocarFormato = (format) => {
    setPipelineFormat(format);
    try { localStorage.setItem("netzer:producao:format:v2", format); } catch { /* Storage can be disabled. */ }
  };

  const load = () => {
    const planning = Promise.all([api.get("/ciclos"), api.get("/clientes"), api.get("/tarefas")])
      .then(([cycles, clients, tasks]) => {
        setCiclos(cycles.data); setClientes(clients.data); setTarefas(tasks.data);
        setPipelineError(""); setPipelineReady(true);
      })
      .catch((error) => setPipelineError(apiError(error)));
    api.get("/equipe").then((r) => setEquipe(r.data)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/eventos").then((r) => setEventos(r.data)).catch(() => {});
    api.get("/postagens").then((r) => setPostagens(r.data)).catch(() => {});
    api.get("/integracoes/google/status").then((r) => setGoogle(r.data)).catch(() => setGoogle({ configurado: false, conectado: false }));
    return planning;
  };
  useEffect(() => { load(); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const alvo = searchParams.get("ciclo");
    if (alvo) {
      setCicloAberto(alvo);
      const next = new URLSearchParams(searchParams);
      next.delete("ciclo");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const requestedView = searchParams.get("view");
  const normalizedView = LEGACY_VIEWS[requestedView] || requestedView;
  const view = VIEWS.includes(normalizedView) ? normalizedView : "pipeline";
  const trocarView = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", value);
    setSearchParams(next, { replace: true });
    setMenuViewsOpen(false);
  };

  const conectarGoogle = async () => {
    try {
      const res = await api.get("/oauth/google/connect");
      window.location.href = res.data.authorization_url;
    } catch (err) { toast.error(apiError(err)); }
  };

  const sincronizarGoogle = async () => {
    setSincronizandoGoogle(true);
    try {
      const result = await syncGoogleEvents();
      toast.success(`${result.sincronizados} sincronizado(s); ${result.pendentes} pendente(s) de revisão nas integrações.`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSincronizandoGoogle(false);
    }
  };

  const nomeCliente = (id) => clientes.find((c) => c.id === id)?.empresa || "—";

  const STATUS_POR_ETAPA = { aprovacao: "aguardando_cliente" };

  const mover = async (c, etapa) => {
    try {
      if (etapa === "finalizado") {
        const res = await api.post(`/ciclos/${c.id}/concluir`);
        toast.success(res.data.proximo_ciclo ? "Ciclo concluído — próximo ciclo criado automaticamente" : "Ciclo concluído");
      } else {
        const payload = { etapa };
        const alvo = STATUS_POR_ETAPA[etapa] || "em_andamento";
        if (c.status !== "bloqueado" && c.status !== alvo) payload.status = alvo;
        await api.patch(`/ciclos/${c.id}`, payload);
        toast.success(`Movido para ${etapaInfo(etapa).nome}${payload.status ? ` · status: ${(STATUS_CICLO[alvo] || {}).nome || alvo}` : ""}`);
      }
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (salvando) return;
    setSalvando(true);
    try {
      await api.post("/ciclos", { ...form, competencia: form.competencia || new Date().toISOString().slice(0, 7) });
      toast.success("Ciclo criado");
      setDialog(false);
      setForm(emptyCiclo);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSalvando(false); }
  };

  // O título sugerido acompanha o mês enquanto ninguém o editou à mão.
  const mudarCompetencia = (competencia) => {
    const cliente = clientes.find((c) => c.id === form.cliente_id);
    const sugerido = cliente && form.titulo === `${cliente.empresa} — ${form.competencia}`;
    setForm({ ...form, competencia, titulo: sugerido ? `${cliente.empresa} — ${competencia}` : form.titulo });
  };

  const gerarMes = async () => {
    try {
      const res = await api.post("/ciclos/gerar-mes");
      toast.success(res.data.criados ? `${res.data.criados} ciclo(s) criado(s) para ${res.data.competencia}` : "Todos os clientes recorrentes já têm ciclo neste mês");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const abrirGravacao = (evento) => {
    setGravacaoEdit(evento);
    setGravacaoForm({ ...evento });
  };

  const salvarGravacao = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/eventos/${gravacaoEdit.id}`, gravacaoForm);
      toast.success("Gravação atualizada no ciclo e no Google Agenda");
      setGravacaoEdit(null);
      setGravacaoForm(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const ListaCiclos = ({ items, vazio }) => (
    <div className="space-y-2">
      {items.length === 0 && <p className="text-sm text-[#71717A] py-4">{vazio}</p>}
      {items.map((c) => {
        const sd = SAUDE[c.saude] || SAUDE.saudavel;
        const etapa = etapaInfo(c.etapa);
        const situacao = c.status === "aguardando_cliente" ? "Aguardando cliente" : etapa.nome;
        return (
          <button key={c.id} data-testid={`lista-ciclo-${c.id}`} onClick={() => setCicloAberto(c.id)} className="studio-card w-full px-4 py-3 flex flex-wrap items-center gap-3 text-left studio-card-hover">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: sd.cor }} />
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm text-[#E4E4E0]">{c.titulo}</p>
              <p className="caption-label normal-case tracking-normal mt-0.5">{nomeCliente(c.cliente_id)} · {c.responsavel || "sem responsável"}</p>
            </div>
            {c.proxima_acao && <span className="text-[11px] text-[#A1A1AA] truncate max-w-[220px]">→ {c.proxima_acao.titulo}</span>}
            <span className="rounded-full border px-2 py-1 font-mono text-[9px] uppercase tracking-wider" style={{ color: etapa.cor, borderColor: `${etapa.cor}55` }}>{situacao}</span>
            <span className="font-mono text-xs text-[#8CC63F]">{fmtData(c.entrega_prevista)}</span>
            <span className="font-mono text-[10px] text-[#71717A]">{c.progresso}%</span>
          </button>
        );
      })}
    </div>
  );

  const ativos = ciclos.filter((c) => c.status !== "concluido");
  const currentView = PRODUCTION_VIEWS.find((item) => item.key === view) || PRODUCTION_VIEWS[1];
  const validacaoCiclos = ativos.filter((c) => ["aprovacao", "programacao", "publicacao"].includes(c.etapa) || c.status === "aguardando_cliente");
  const validacaoPosts = postagens.filter((p) => ["em_aprovacao", "ajustes_solicitados", "aprovado", "planejado", "agendado"].includes(p.status));

  return (
    <div className="space-y-6" data-testid="producao-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <Sheet open={menuViewsOpen} onOpenChange={setMenuViewsOpen}>
            <SheetTrigger asChild>
              <button type="button" data-testid="production-views-menu" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#27272A] bg-[#141418] text-[#A1A1AA] transition-colors hover:border-[#8CC63F]/50 hover:text-[#8CC63F]" aria-label="Abrir visualizações da produção">
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[min(88vw,22rem)] border-r border-[#27272A] bg-[#0e0e11] text-[#F4F4F0]">
              <SheetHeader>
                <SheetTitle className="font-serif text-2xl text-[#F4F4F0]">Hub de <span className="gold-italic">Produção</span></SheetTitle>
                <SheetDescription className="sr-only">Escolha uma visualização para trabalhar no Hub de Produção.</SheetDescription>
              </SheetHeader>
              <nav className="mt-7 space-y-2" aria-label="Visualizações da produção">
                {PRODUCTION_VIEWS.map((item) => {
                  const Icon = item.icon;
                  const active = item.key === view;
                  return (
                    <button key={item.key} type="button" data-testid={`view-${item.key}`} onClick={() => trocarView(item.key)} className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${active ? "border-[#8CC63F]/40 bg-[#8CC63F]/10 text-[#8CC63F]" : "border-transparent text-[#A1A1AA] hover:border-[#27272A] hover:bg-[#18181C] hover:text-[#F4F4F0]"}`}>
                      <Icon size={18} className="shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">{item.label}</span>
                        <span className="mt-0.5 block text-[11px] font-normal text-[#71717A]">{item.description}</span>
                      </span>
                    </button>
                  );
                })}
              </nav>
            </SheetContent>
          </Sheet>
          <div>
            <p className="caption-label mb-2">{view === "hoje" ? <>Produção<span className="hidden sm:inline"> · Tarefas da equipe</span></> : `01 // Operação · ${currentView.label}`}</p>
            <h1 className={`${view === "hoje" ? "font-sans font-bold" : "font-serif"} text-3xl sm:text-4xl text-[#F4F4F0] tracking-tight`}>{view === "hoje" ? "Hoje" : <>Hub de <span className="gold-italic">Produção</span></>}</h1>
          </div>
        </div>
        {view === "pipeline" && (
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <Button data-testid="gerar-ciclos-button" onClick={gerarMes} variant="outline" className="flex-1 border-[#8CC63F]/40 text-[#8CC63F] hover:bg-[#8CC63F]/10 rounded-full sm:flex-none">
              <Zap size={15} className="mr-1" /> Gerar ciclos do mês
            </Button>
            <Button data-testid="novo-ciclo-button" onClick={() => setDialog(true)} className="flex-1 bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold sm:flex-none">
              <Plus size={16} className="mr-1" /> Novo ciclo
            </Button>
          </div>
        )}
      </div>

      <Tabs value={view}>
        <TabsContent value="hoje" className="mt-0">
          <Hoje embedded />
        </TabsContent>

        <TabsContent value="clientes" className="mt-0">
          <Clientes embedded />
        </TabsContent>

        <TabsContent value="pipeline" className="mt-0">
          <div className="mb-5 inline-flex rounded-full border border-[#27272A] bg-[#141418] p-1" role="group" aria-label="Formato da produção">
            {[["resumo", "Por cliente"], ["kanban", "Kanban"], ["tabela", "Tabela detalhada"]].map(([key, label]) => (
              <button key={key} type="button" data-testid={`pipeline-format-${key}`} aria-pressed={pipelineFormat === key} onClick={() => trocarFormato(key)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#8CC63F] ${pipelineFormat === key ? "bg-[#8CC63F]/10 text-[#8CC63F]" : "text-[#A1A1AA] hover:text-[#F4F4F0]"}`}>{label}</button>
            ))}
          </div>
          {pipelineError ? <div role="alert" className="studio-card p-5"><p>{pipelineError}</p><Button variant="outline" onClick={load} className="mt-3">Tentar novamente</Button></div>
            : !pipelineReady ? <p role="status" className="text-sm text-[#A1A1AA]">Carregando planejamento…</p>
            : pipelineFormat === "resumo" ? <ProducaoResumo clientes={clientes} ciclos={ciclos} tarefas={tarefas} onOpenCycle={setCicloAberto} onChange={load}
              onCreateCycle={(cliente, competencia) => { setForm({ ...emptyCiclo, cliente_id: cliente.id, competencia, titulo: `${cliente.empresa} — ${competencia}`, tipo: cliente.tipo === "recorrente" ? "mensal" : "pontual" }); setDialog(true); }} />
            : pipelineFormat === "tabela" ? <ProducaoClientesTable clientes={clientes} ciclos={ciclos} tarefas={tarefas} equipe={equipe} onChange={load} onOpenCycle={setCicloAberto}
              onCreateCycle={(cliente, competencia) => { setForm({ ...emptyCiclo, cliente_id: cliente.id, competencia, titulo: `${cliente.empresa} — ${competencia}`, tipo: cliente.tipo === "recorrente" ? "mensal" : "pontual" }); setDialog(true); }} />
            : <ProducaoKanban clientes={clientes} ciclos={ciclos} tarefas={tarefas} onOpenCycle={setCicloAberto} onMove={mover} />}
        </TabsContent>

        <TabsContent value="gravacoes" className="mt-0">
          <div className="space-y-6">
            <GravacoesCalendar eventos={eventos} tarefas={tarefas} clientes={clientes} equipe={equipe} onEdit={abrirGravacao} onOpenCycle={setCicloAberto} />
            <div>
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="caption-label">Lista de gravações</p>
                <span className="font-mono text-[11px] text-[#71717A]">{eventos.filter((e) => ["gravacao_estudio", "captacao_externa"].includes(e.tipo)).length} registro(s)</span>
              </div>
              <div className="space-y-2">
                {eventos.filter((e) => ["gravacao_estudio", "captacao_externa"].includes(e.tipo)).length === 0 && <p className="text-sm text-[#71717A]">Nenhuma gravação agendada.</p>}
                {eventos.filter((e) => ["gravacao_estudio", "captacao_externa"].includes(e.tipo)).map((e) => {
                  const sg = STATUS_GRAVACAO[e.status] || STATUS_GRAVACAO.agendada;
                  return (
                    <div key={e.id} data-testid={`gravacao-row-${e.id}`} className="studio-card px-4 py-3 flex flex-wrap items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tipoEventoInfo(e.tipo).cor }} />
                      <div className="flex-1 min-w-[180px]">
                        <p className="text-sm text-[#E4E4E0]">{e.titulo}</p>
                        <p className="caption-label normal-case tracking-normal mt-0.5">{nomeCliente(e.cliente_id)}{e.local ? ` · ${e.local}` : ""}</p>
                      </div>
                      <span className="font-mono text-xs text-[#8CC63F]">{fmtData(e.data)} {e.hora_inicio}</span>
                      <Select value={e.status || "agendada"} onValueChange={async (v) => { await api.patch(`/eventos/${e.id}`, { status: v }); load(); }}>
                        <SelectTrigger data-testid={`gravacao-status-${e.id}`} className="w-36 h-8 bg-[#18181C] border-[#27272A] text-xs font-mono" style={{ color: sg.cor }}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                          {Object.entries(STATUS_GRAVACAO).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button data-testid={`gravacao-editar-${e.id}`} onClick={() => abrirGravacao(e)} className="p-2 text-[#71717A] hover:text-[#8CC63F]" title="Editar gravação" aria-label={`Editar ${e.titulo}`}>
                        <Pencil size={15} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="edicao" className="mt-0">
          <ListaCiclos items={ativos.filter((c) => c.etapa === "pos_producao")} vazio="Nada em pós-produção." />
        </TabsContent>

        <TabsContent value="validacao" className="mt-0 space-y-6">
          <section>
            <div className="mb-3 flex items-end justify-between gap-4">
              <div>
                <p className="caption-label">Fluxo unificado</p>
                <h2 className="mt-1 font-serif text-2xl text-[#F4F4F0]">Validação e <span className="gold-italic">entrega</span></h2>
              </div>
              <span className="font-mono text-xs text-[#71717A]">{validacaoCiclos.length} ciclo(s)</span>
            </div>
            <ListaCiclos items={validacaoCiclos} vazio="Nenhum ciclo aguardando cliente, aprovação ou publicação." />
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between gap-4">
              <p className="caption-label">Conteúdos que precisam avançar</p>
              <Button asChild variant="outline" size="sm" className="rounded-full border-[#27272A] text-[#A1A1AA]">
                <Link to="/calendario-editorial">Abrir calendário editorial</Link>
              </Button>
            </div>
            <div className="space-y-2">
              {validacaoPosts.slice(0, 20).map((p) => {
                const ps = POST_STATUS[p.status] || POST_STATUS.planejado;
                return (
                  <div key={p.id} data-testid={`validacao-post-${p.id}`} className="studio-card flex flex-wrap items-center gap-3 px-4 py-3">
                    <div className="min-w-[180px] flex-1">
                      <p className="text-sm text-[#E4E4E0]">{p.titulo}</p>
                      <p className="caption-label mt-0.5 normal-case tracking-normal">{nomeCliente(p.cliente_id)}{p.rede_social ? ` · ${p.rede_social}` : ""}</p>
                    </div>
                    <span className="font-mono text-xs text-[#8CC63F]">{fmtData(p.data)}</span>
                    <span className="rounded-full border px-2 py-1 font-mono text-[10px] uppercase tracking-widest" style={{ color: ps.cor, borderColor: `${ps.cor}55` }}>{ps.nome}</span>
                    {p.status === "em_aprovacao" && (
                      <Button size="sm" data-testid={`aprovar-post-${p.id}`} onClick={async () => { await api.patch(`/postagens/${p.id}`, { status: "aprovado" }); toast.success("Conteúdo aprovado"); load(); }} className="rounded-full border border-[#10B981]/30 bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25">
                        <Check size={13} className="mr-1" /> Aprovar
                      </Button>
                    )}
                  </div>
                );
              })}
              {validacaoPosts.length === 0 && <p className="text-sm text-[#71717A]">Nenhum conteúdo aguardando validação ou entrega.</p>}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="agenda" className="mt-0">
          <div className="studio-card p-5 sm:p-6" data-testid="google-agenda-panel">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#8CC63F]/30 bg-[#8CC63F]/10">
                  <CalendarDays size={21} className="text-[#8CC63F]" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-serif text-2xl text-[#F4F4F0]">Google <span className="gold-italic">Agenda</span></h2>
                    <span className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${google?.conectado ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981]" : "border-[#27272A] text-[#71717A]"}`}>
                      {google?.conectado ? "Conectado" : "Desconectado"}
                    </span>
                  </div>
                  <p className="mt-2 max-w-2xl text-sm text-[#A1A1AA]">Reuniões, gravações e entregas reconhecidas nas tarefas são sincronizadas automaticamente. A agenda passa a ser a referência de compromissos do estúdio.</p>
                  {google?.conectado && (
                    <p className="mt-2 font-mono text-xs text-[#10B981]">
                      {google.email}{typeof google.eventos_sincronizados === "number" ? ` · ${google.eventos_sincronizados} compromisso(s) vinculado(s)` : ""}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:min-w-48">
                {google?.conectado ? (
                  <>
                    <Button asChild className="rounded-full bg-[#8CC63F] font-semibold text-[#09090B] hover:bg-[#A9DD6B]">
                      <a href="https://calendar.google.com/calendar/u/0/r" target="_blank" rel="noreferrer"><ExternalLink size={15} className="mr-1" /> Abrir Google Agenda</a>
                    </Button>
                    <Button onClick={sincronizarGoogle} disabled={sincronizandoGoogle} variant="outline" className="rounded-full border-[#8CC63F]/40 text-[#8CC63F] hover:bg-[#8CC63F]/10">
                      {sincronizandoGoogle ? "Sincronizando…" : "Sincronizar compromissos"}
                    </Button>
                  </>
                ) : google?.configurado ? (
                  <Button onClick={conectarGoogle} className="rounded-full bg-[#8CC63F] font-semibold text-[#09090B] hover:bg-[#A9DD6B]">Conectar Google</Button>
                ) : (
                  <Button asChild variant="outline" className="rounded-full border-[#8CC63F]/40 text-[#8CC63F] hover:bg-[#8CC63F]/10">
                    <Link to="/configuracoes?tab=integracoes">Configurar integração</Link>
                  </Button>
                )}
                <Button asChild variant="outline" className="rounded-full border-[#27272A] text-[#A1A1AA]">
                  <Link to="/producao?view=gravacoes">Ver gravações no sistema</Link>
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

      </Tabs>

      <CicloDrawer cicloId={cicloAberto} onClose={() => setCicloAberto(null)} onChange={load} equipe={equipe} templates={templates} />

      <Dialog open={!!gravacaoEdit} onOpenChange={(open) => { if (!open) { setGravacaoEdit(null); setGravacaoForm(null); } }}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg max-h-[90vh] overflow-y-auto">
          {gravacaoForm && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-2xl">Editar <span className="gold-italic">gravação</span></DialogTitle></DialogHeader>
              <form onSubmit={salvarGravacao} className="space-y-4 mt-2" data-testid="gravacao-edit-form">
                <div><Label className="caption-label">Título</Label>
                  <Input required value={gravacaoForm.titulo || ""} onChange={(e) => setGravacaoForm({ ...gravacaoForm, titulo: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label className="caption-label">Data</Label>
                    <Input data-testid="gravacao-edit-data" required type="date" value={gravacaoForm.data || ""} onChange={(e) => setGravacaoForm({ ...gravacaoForm, data: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                  <div><Label className="caption-label">Tipo</Label>
                    <Select value={gravacaoForm.tipo || "captacao_externa"} onValueChange={(v) => setGravacaoForm({ ...gravacaoForm, tipo: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                        <SelectItem value="gravacao_estudio">Gravação no Estúdio</SelectItem>
                        <SelectItem value="captacao_externa">Captação Externa</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label className="caption-label">Início</Label>
                    <Input data-testid="gravacao-edit-inicio" type="time" value={gravacaoForm.hora_inicio || ""} onChange={(e) => setGravacaoForm({ ...gravacaoForm, hora_inicio: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                  <div><Label className="caption-label">Fim</Label>
                    <Input type="time" value={gravacaoForm.hora_fim || ""} onChange={(e) => setGravacaoForm({ ...gravacaoForm, hora_fim: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                </div>
                <div><Label className="caption-label">Cliente</Label>
                  <Select value={gravacaoForm.cliente_id || "none"} onValueChange={(v) => setGravacaoForm({ ...gravacaoForm, cliente_id: v === "none" ? "" : v, ciclo_id: "", projeto_id: "" })}>
                    <SelectTrigger className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      <SelectItem value="none">Sem cliente</SelectItem>
                      {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.empresa}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label className="caption-label">Ciclo vinculado</Label>
                  <Select value={gravacaoForm.ciclo_id || gravacaoForm.projeto_id || "none"} onValueChange={(v) => setGravacaoForm({ ...gravacaoForm, ciclo_id: v === "none" ? "" : v, projeto_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="gravacao-edit-ciclo" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="Vincular ao ciclo" /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      <SelectItem value="none">Sem ciclo</SelectItem>
                      {ativos.filter((c) => !gravacaoForm.cliente_id || c.cliente_id === gravacaoForm.cliente_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.titulo}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label className="caption-label">Local</Label>
                    <Input value={gravacaoForm.local || ""} onChange={(e) => setGravacaoForm({ ...gravacaoForm, local: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                  <div><Label className="caption-label">Status</Label>
                    <Select value={gravacaoForm.status || "agendada"} onValueChange={(v) => setGravacaoForm({ ...gravacaoForm, status: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                        {Object.entries(STATUS_GRAVACAO).map(([k, v]) => <SelectItem key={k} value={k}>{v.nome}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                </div>
                <div><Label className="caption-label">Responsável</Label>
                  <Select value={gravacaoForm.responsavel || "none"} onValueChange={(v) => setGravacaoForm({ ...gravacaoForm, responsavel: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                      <SelectItem value="none">—</SelectItem>
                      {equipe.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <Button data-testid="gravacao-edit-salvar" type="submit" className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">Salvar e sincronizar</Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Novo <span className="gold-italic">ciclo</span></DialogTitle></DialogHeader>
          <form onSubmit={salvar} className="space-y-4 mt-2">
            <div><Label className="caption-label">Cliente</Label>
              <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })} required>
                <SelectTrigger data-testid="ciclo-cliente-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.empresa}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="caption-label">Título</Label>
              <Input data-testid="ciclo-titulo-input" required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: Setembro/2026 ou Campanha de Natal" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Competência</Label>
                <Input data-testid="ciclo-competencia-input" type="month" value={form.competencia} onChange={(e) => mudarCompetencia(e.target.value)} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="ciclo-tipo-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {TIPOS_CICLO.map((t) => <SelectItem key={t.key} value={t.key}>{t.nome}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="caption-label">Template operacional (cria as tarefas)</Label>
              <Select value={form.template_id || "none"} onValueChange={(v) => setForm({ ...form, template_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ciclo-template-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="Sem template" /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  <SelectItem value="none">Sem template</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome} ({t.tarefas.length} tarefas)</SelectItem>)}
                </SelectContent>
              </Select></div>
            <p className="caption-label normal-case tracking-normal -mt-2">Os templates operacionais ficam em <Link to="/configuracoes" data-testid="ciclo-link-templates" className="text-[#8CC63F] hover:text-[#A9DD6B]">Configurações → Templates operacionais</Link> — lá você confere, edita ou cria novos.</p>
            {form.template_id && (() => {
              const tpl = templates.find((t) => t.id === form.template_id);
              if (!tpl) return null;
              return (
                <div className="border border-[#27272A] rounded-lg p-3 max-h-48 overflow-y-auto -mt-2" data-testid="ciclo-template-preview">
                  <p className="caption-label mb-2">Tarefas do ciclo · {tpl.tarefas.length}{tpl.tarefas.some((t) => t.somente_mes_captacao) ? " · alternância bimestral automática" : ""}</p>
                  <div className="space-y-1.5">
                    {tpl.tarefas.map((t, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-[#8CC63F] shrink-0 w-9">D+{t.offset_dias ?? 1}</span>
                        <span className="text-xs text-[#E4E4E0] truncate flex-1">{t.titulo}{t.somente_mes_captacao ? " · mês de captação" : ""}</span>
                        <span className="font-mono text-[9px] text-[#71717A] shrink-0">{etapaInfo(t.etapa).nome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Responsável</Label>
                <Select value={form.responsavel || "none"} onValueChange={(v) => setForm({ ...form, responsavel: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="ciclo-resp-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="none">—</SelectItem>
                    {equipe.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="caption-label">Entrega prevista</Label>
                <Input data-testid="ciclo-entrega-input" type="date" value={form.entrega_prevista} onChange={(e) => setForm({ ...form, entrega_prevista: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            </div>
            <Button data-testid="ciclo-submit-button" type="submit" disabled={salvando} className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">{salvando ? "Criando…" : "Criar ciclo"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
